import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Check, X } from 'lucide-react';

const comparisons = [
  { aspect: 'Product context', traditional: 'Treats you like a generic software vendor', erlichman: 'Understands how your platform functions' },
  { aspect: 'Domain expertise', traditional: 'No life sciences or regulated data experience', erlichman: 'Deep familiarity with pharma/biotech workflows' },
  { aspect: 'Who does the work', traditional: 'Junior associates who lack context', erlichman: 'Senior counsel embedded in your business' },
  { aspect: 'Internal engagement', traditional: 'Broad questions that slow deals down', erlichman: 'Targeted questions that produce actionable input' },
  { aspect: 'Contract positions', traditional: 'Reinvented for each deal', erlichman: 'Playbook aligned with your products and risk tolerance' },
  { aspect: 'Result', traditional: 'Over-lawyering, escalations, inconsistency', erlichman: 'Faster cycles, consistent outcomes' },
];

export const WhyUsPage = () => {
  return (
    <div data-testid="why-us-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20">
        <div className="container-law">
          <div className="breadcrumb text-white/60 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Why Us</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">Why Erlichman Law</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            The depth of an in-house legal team without the overhead—and without the inefficiencies and learning curve of traditional outside counsel.
          </p>
        </div>
      </section>

      {/* Core Differentiator */}
      <section className="py-16 md:py-20">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="font-heading text-3xl text-navy mb-6">The Problem with Traditional Outside Counsel</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Traditional outside firms often approach these deals without product or domain context, resulting in over-lawyering, unnecessary escalations, and inconsistent positions. They treat life sciences technology companies like generic software vendors.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Junior associates who lack context ask broad questions that slow deals down. Positions get reinvented for each transaction. Counterparties don't understand the rationale behind your terms. Negotiations drag on.
              </p>
            </div>
            <div>
              <h2 className="font-heading text-3xl text-navy mb-6">Our Model is Different</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                We take the time to understand how your platforms function, how data is generated and processed, how services are delivered, and how customers actually use your products in clinical, regulatory, or commercial workflows.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We act as an extension of your internal legal and business teams. We know the recurring issues, the risk tolerances, the internal decision makers, and the business rationale behind each position. That institutional knowledge reduces friction, shortens deal cycles, and produces consistent outcomes.
              </p>
            </div>
          </div>

          {/* Comparison Table */}
          <h2 className="font-heading text-3xl text-navy mb-8">Traditional Firms vs. Erlichman Law</h2>
          <div className="overflow-x-auto border border-gray-200">
            <table className="w-full min-w-[600px]" data-testid="comparison-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 uppercase tracking-wider"></th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 uppercase tracking-wider">Traditional Firms</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-green uppercase tracking-wider">Erlichman Law</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="py-4 px-6 font-medium text-navy">{row.aspect}</td>
                    <td className="py-4 px-6 text-gray-600 text-sm">{row.traditional}</td>
                    <td className="py-4 px-6 text-navy text-sm font-medium">{row.erlichman}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Good Fit / Not Fit */}
      <section className="py-16 bg-gray-50">
        <div className="container-law">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 p-8">
              <h3 className="font-heading text-xl text-navy mb-6 flex items-center gap-2">
                <Check className="w-5 h-5 text-green" strokeWidth={2} />
                A Good Fit For
              </h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="font-medium text-navy">Life sciences technology vendors</span>
                    <p className="text-gray-600 text-sm mt-1">Companies selling software, data, or services to pharmaceutical and biotech enterprises.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="font-medium text-navy">Companies with complex deal structures</span>
                    <p className="text-gray-600 text-sm mt-1">Businesses combining SaaS, professional services, data licensing, and long-term engagements.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="font-medium text-navy">Teams that need inside counsel depth without the overhead</span>
                    <p className="text-gray-600 text-sm mt-1">Organizations looking for an integrated legal function embedded in their business.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 p-8">
              <h3 className="font-heading text-xl text-navy mb-6 flex items-center gap-2">
                <X className="w-5 h-5 text-gray-400" strokeWidth={2} />
                What We Don't Do
              </h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="font-medium text-navy">Litigation or disputes</span>
                    <p className="text-gray-600 text-sm mt-1">We focus on commercial transactions, not courtroom work.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="font-medium text-navy">Consumer-facing legal matters</span>
                    <p className="text-gray-600 text-sm mt-1">We work with businesses, not individuals.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <span className="font-medium text-navy">Generic software vendor work</span>
                    <p className="text-gray-600 text-sm mt-1">We focus on life sciences and regulated technology markets.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container-law">
          <div className="bg-navy p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl text-white mb-2">Think we might be a fit?</h2>
              <p className="text-white/70">Let's have a conversation about your business and your commercial needs.</p>
            </div>
            <Link to="/contact" className="btn-green whitespace-nowrap" data-testid="why-us-cta-btn">
              Get in Touch <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
